import { Type } from "@sinclair/typebox";
import { IdSchema, JsonValueSchema, NonNegativeIntegerSchema, Sha256Schema, TimestampSchema, } from "./shared.js";
import { PresentationSnapshotSchema } from "./presentation.js";
import { FileInputManifestSchema } from "./files.js";
export const BudgetSchema = Type.Object({
    cpuMillis: NonNegativeIntegerSchema,
    memoryBytes: NonNegativeIntegerSchema,
    inputBytes: NonNegativeIntegerSchema,
    outputBytes: NonNegativeIntegerSchema,
    externalCalls: NonNegativeIntegerSchema,
}, { additionalProperties: false });
export const AuthorizationEnvelopeSchema = Type.Object({
    envelopeId: IdSchema,
    identityId: IdSchema,
    sessionId: IdSchema,
    turnId: IdSchema,
    generation: NonNegativeIntegerSchema,
    goalDigest: Sha256Schema,
    disclosureReceiptId: IdSchema,
    targetDigest: Sha256Schema,
    budget: BudgetSchema,
    allowedRisk: Type.Literal("low"),
    envelopeDigest: Sha256Schema,
    createdAt: TimestampSchema,
    expiresAt: TimestampSchema,
}, { additionalProperties: false });
export const ActionRequestSchema = Type.Object({
    actionId: IdSchema,
    identityId: IdSchema,
    sessionId: IdSchema,
    generation: NonNegativeIntegerSchema,
    serverId: IdSchema,
    functionName: Type.String({ minLength: 1, maxLength: 256 }),
    functionSchemaDigest: Sha256Schema,
    approvalClassDigest: Sha256Schema,
    arguments: JsonValueSchema,
    argumentsDigest: Sha256Schema,
    inputManifest: Type.Union([FileInputManifestSchema, Type.Null()]),
    inputManifestDigest: Type.Union([Sha256Schema, Type.Null()]),
    envelopeDigest: Sha256Schema,
    targetDigest: Sha256Schema,
    budget: BudgetSchema,
    createdAt: TimestampSchema,
    deadline: TimestampSchema,
    idempotencyKey: Type.String({ minLength: 16, maxLength: 256 }),
    risk: Type.Union([Type.Literal("low"), Type.Literal("high")]),
    requestDigest: Sha256Schema,
}, { additionalProperties: false });
export const ActionGrantSchema = Type.Object({
    grantId: IdSchema,
    actionId: IdSchema,
    requestDigest: Sha256Schema,
    identityId: IdSchema,
    sessionId: IdSchema,
    generation: NonNegativeIntegerSchema,
    serverId: IdSchema,
    functionName: Type.String({ minLength: 1, maxLength: 256 }),
    functionSchemaDigest: Sha256Schema,
    argumentsDigest: Sha256Schema,
    inputManifestDigest: Type.Union([Sha256Schema, Type.Null()]),
    envelopeDigest: Sha256Schema,
    targetDigest: Sha256Schema,
    budget: BudgetSchema,
    deadline: TimestampSchema,
    idempotencyKey: Type.String({ minLength: 16, maxLength: 256 }),
    issuedAt: TimestampSchema,
    expiresAt: TimestampSchema,
}, { additionalProperties: false });
export const ActionCostReceiptSchema = Type.Object({
    grant: ActionGrantSchema,
    actualCost: BudgetSchema,
}, { additionalProperties: false });
export const ObservationSchema = Type.Object({
    observationId: IdSchema,
    actionId: IdSchema,
    grantId: IdSchema,
    untrusted: Type.Literal(true),
    status: Type.Union([
        Type.Literal("succeeded"),
        Type.Literal("failed"),
        Type.Literal("cancelled"),
        Type.Literal("timed_out"),
    ]),
    result: JsonValueSchema,
    resultDigest: Sha256Schema,
    presentation: Type.Optional(PresentationSnapshotSchema),
    actualCost: BudgetSchema,
    completedAt: TimestampSchema,
}, { additionalProperties: false });
const AuditRecordBaseSchema = {
    recordId: IdSchema,
    identityId: IdSchema,
    subjectId: IdSchema,
    payloadDigest: Sha256Schema,
    occurredAt: TimestampSchema,
};
const RawArtifactAuditPayloadSchema = Type.Object({ allowRawArtifacts: Type.Boolean() }, { additionalProperties: false });
const DeletionStages = [
    "pending",
    "authorizations_revoked",
    "tools_stopped",
    "tools_absent",
    "runtime_stopped",
    "runtime_absent",
    "tool_storage_deleted",
    "session_storage_deleted",
    "database_deleted",
    "complete",
];
export const AuditRecordSchema = Type.Union([
    Type.Object({
        ...AuditRecordBaseSchema,
        event: Type.Literal("identity.user_activated_by_administrator"),
        payload: Type.Object({ status: Type.Literal("active") }, { additionalProperties: false }),
    }, { additionalProperties: false }),
    Type.Object({
        ...AuditRecordBaseSchema,
        event: Type.Literal("identity.registered"),
        payload: RawArtifactAuditPayloadSchema,
    }, { additionalProperties: false }),
    Type.Object({
        ...AuditRecordBaseSchema,
        event: Type.Literal("identity.administrator_bootstrapped"),
        payload: Type.Object({ rawArtifactConsentDeferred: Type.Literal(true) }, { additionalProperties: false }),
    }, { additionalProperties: false }),
    Type.Object({
        ...AuditRecordBaseSchema,
        event: Type.Literal("identity.administrator_bootstrap_completed"),
        payload: RawArtifactAuditPayloadSchema,
    }, { additionalProperties: false }),
    Type.Object({
        ...AuditRecordBaseSchema,
        event: Type.Literal("identity.raw_artifact_consent_granted"),
        payload: Type.Object({ allowRawArtifacts: Type.Literal(true) }, { additionalProperties: false }),
    }, { additionalProperties: false }),
    Type.Object({
        ...AuditRecordBaseSchema,
        event: Type.Literal("identity.raw_artifact_consent_revoked"),
        payload: Type.Object({ allowRawArtifacts: Type.Literal(false) }, { additionalProperties: false }),
    }, { additionalProperties: false }),
    Type.Object({
        ...AuditRecordBaseSchema,
        event: Type.Literal("identity.user_created_by_administrator"),
        payload: RawArtifactAuditPayloadSchema,
    }, { additionalProperties: false }),
    Type.Object({
        ...AuditRecordBaseSchema,
        event: Type.Literal("identity.password_reset_by_administrator"),
        payload: Type.Object({ browserSessionsRevoked: Type.Literal(true) }, { additionalProperties: false }),
    }, { additionalProperties: false }),
    Type.Object({
        ...AuditRecordBaseSchema,
        event: Type.Literal("identity.password_changed"),
        payload: Type.Object({ browserSessionsRevoked: Type.Literal(true) }, { additionalProperties: false }),
    }, { additionalProperties: false }),
    Type.Object({
        ...AuditRecordBaseSchema,
        event: Type.Literal("identity.user_deletion_requested"),
        payload: Type.Object({ status: Type.Literal("disabled") }, { additionalProperties: false }),
    }, { additionalProperties: false }),
    Type.Object({
        ...AuditRecordBaseSchema,
        event: Type.Literal("identity.user_deleted_by_administrator"),
        payload: Type.Object({ status: Type.Literal("deleted") }, { additionalProperties: false }),
    }, { additionalProperties: false }),
    Type.Object({
        ...AuditRecordBaseSchema,
        event: Type.Literal("session.deletion_requested"),
        payload: Type.Object({
            jobId: IdSchema,
            sessionId: IdSchema,
            stage: Type.Literal("pending"),
        }, { additionalProperties: false }),
    }, { additionalProperties: false }),
    Type.Object({
        ...AuditRecordBaseSchema,
        event: Type.Literal("session.deletion_stage_transitioned"),
        payload: Type.Object({
            jobId: IdSchema,
            sessionId: IdSchema,
            from: Type.Union(DeletionStages.map((stage) => Type.Literal(stage))),
            to: Type.Union(DeletionStages.map((stage) => Type.Literal(stage))),
            attempts: NonNegativeIntegerSchema,
        }, { additionalProperties: false }),
    }, { additionalProperties: false }),
]);
export const ActionMessageBodySchema = Type.Union([
    Type.Object({ kind: Type.Literal("envelope"), value: AuthorizationEnvelopeSchema }, { additionalProperties: false }),
    Type.Object({ kind: Type.Literal("request"), value: ActionRequestSchema }, { additionalProperties: false }),
    Type.Object({ kind: Type.Literal("grant"), value: ActionGrantSchema }, { additionalProperties: false }),
    Type.Object({ kind: Type.Literal("cost_receipt"), value: ActionCostReceiptSchema }, { additionalProperties: false }),
    Type.Object({ kind: Type.Literal("observation"), value: ObservationSchema }, { additionalProperties: false }),
    Type.Object({ kind: Type.Literal("audit_record"), value: AuditRecordSchema }, { additionalProperties: false }),
]);
//# sourceMappingURL=action.js.map